import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useApolloClient, useQuery } from '@apollo/client/react'

import { useAuth } from 'src/auth'
import { QUERY } from 'src/components/App2Cases/app2WorklistQuery'
import {
  readRecentApplications,
  recentApplicationsKey,
  rememberRecentApplication,
} from 'src/lib/app2RecentApplications'
import {
  DEFAULT_WORKLIST_MAX_AGE_DAYS,
  rankWorklist,
  splitWorklistBands,
} from 'src/lib/app2WorklistUtils'
import { navigate } from 'src/platform/router'
import type {
  App2WorklistQuery,
  App2WorklistQueryVariables,
} from 'types/graphql'

const useCasesState = (applicationId: number | null) => {
  const client = useApolloClient()
  const { currentUser } = useAuth()
  const userId = currentUser?.id
  const owner =
    typeof userId === 'number' || typeof userId === 'string' ? userId : null
  const [recent, setRecent] = useState<{
    owner: string | number | null
    ids: number[]
  }>({ owner: null, ids: [] })
  const recentIds = recent.owner === owner ? recent.ids : []
  const [recentExpanded, setRecentExpanded] = useState(false)
  const [otherExpanded, setOtherExpandedState] = useState(false)
  const [otherRequested, setOtherRequested] = useState(false)
  const [requestedFor, setRequestedFor] = useState<typeof owner>(null)
  const requestWorklist = useCallback(() => setRequestedFor(owner), [owner])
  const setOtherExpanded = (expanded: boolean) => {
    setOtherExpandedState(expanded)
    if (expanded) setOtherRequested(true)
  }
  const [applicationNumber, setApplicationNumber] = useState('')
  const [invalidApplicationNumber, setInvalidApplicationNumber] =
    useState(false)
  const [showOlder, setShowOlder] = useState(false)
  const scrollTop = useRef(0)
  const actionableOnly = !otherRequested && !showOlder
  const { data, previousData, loading, error, refetch } = useQuery<
    App2WorklistQuery,
    App2WorklistQueryVariables
  >(QUERY, {
    variables: {
      actionableOnly,
      ...(showOlder ? { maxAgeDays: null } : {}),
    },
    skip: owner === null || requestedFor !== owner,
    fetchPolicy: actionableOnly ? 'cache-and-network' : 'cache-first',
  })
  const visibleData = data ?? previousData
  const bands = useMemo(
    () =>
      splitWorklistBands(
        rankWorklist(visibleData?.app2Worklist.items ?? []),
        showOlder ? DEFAULT_WORKLIST_MAX_AGE_DAYS : null
      ),
    [visibleData, showOlder]
  )

  const loadWorklistOrder = useCallback(async () => {
    // The next-case shortcut also needs cases in the collapsed Other group.
    const { data } = await client.query<
      App2WorklistQuery,
      App2WorklistQueryVariables
    >({
      query: QUERY,
      variables: {
        actionableOnly: false,
        ...(showOlder ? { maxAgeDays: null } : {}),
      },
      fetchPolicy: 'cache-first',
    })
    setOtherRequested(true)
    const { people, pipes, older } = splitWorklistBands(
      rankWorklist(data?.app2Worklist.items ?? []),
      showOlder ? DEFAULT_WORKLIST_MAX_AGE_DAYS : null
    )
    return [...people, ...pipes, ...older].map((item) => item.applicationId)
  }, [client, showOlder])

  useEffect(() => {
    setRecentExpanded(false)
    setOtherExpandedState(false)
    setOtherRequested(false)
    setShowOlder(false)
    setApplicationNumber('')
    setInvalidApplicationNumber(false)
    scrollTop.current = 0
  }, [owner])
  useEffect(() => {
    setRecent({
      owner,
      ids:
        owner === null
          ? []
          : applicationId === null
            ? readRecentApplications(owner)
            : rememberRecentApplication(owner, applicationId),
    })
  }, [applicationId, owner])
  useEffect(() => {
    if (owner === null) return
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === recentApplicationsKey(owner)) {
        setRecent({ owner, ids: readRecentApplications(owner) })
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [owner])

  return {
    applicationId,
    applicationNumber,
    setApplicationNumber,
    invalidApplicationNumber,
    setInvalidApplicationNumber,
    recentIds,
    recentExpanded,
    setRecentExpanded,
    otherExpanded,
    setOtherExpanded,
    otherRequested,
    requestWorklist,
    loadWorklistOrder,
    showOlder,
    setShowOlder,
    scrollTop,
    data: visibleData,
    loading,
    error,
    refetch,
    bands,
    navigateTo: navigate,
  }
}

const CasesContext = createContext<ReturnType<typeof useCasesState> | null>(
  null
)

// Mounted once by the route layout, so the query, folds, input and scroll
// survive switching between the fleet and any case route.
export const App2CasesProvider = ({
  applicationId,
  children,
}: {
  applicationId: number | null
  children: React.ReactNode
}) => (
  <CasesContext.Provider value={useCasesState(applicationId)}>
    {children}
  </CasesContext.Provider>
)

export const useApp2Cases = () => {
  const context = useContext(CasesContext)
  if (!context)
    throw new Error('Cases must be rendered inside App2CasesProvider')
  return context
}
